import { Component, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy, DestroyRef, inject, ViewChild, ElementRef, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';
import { LazyChartComponent } from 'src/shared/components/lazy-chart/lazy-chart.component';

import { NoiseDetailPage } from 'src/pages/compliance/noise-detail/noise-detail';
import { HardwareInfoPage } from 'src/pages/compliance/hardware-info/hardware-info.page';
import { OrderSensorComponent } from 'src/shared/components/order-sensor/order-sensor.component';
import { SensorPromoCardComponent } from 'src/shared/components/sensor-promo-card/sensor-promo-card.component';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { DemoDataService } from 'src/shared/services/demo-data.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

@Component({
  selector: 'noise-monitoring-content',
  templateUrl: './noise-monitoring-content.component.html',
  styleUrls: ['./noise-monitoring-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
    LazyChartComponent,
    SensorPromoCardComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class NoiseMonitoringContentComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  private aboutThisPageService = inject(AboutThisPageService);
  private demoDataService = inject(DemoDataService);
  private demoBlock = inject(DemoBlockService);
  private cdr = inject(ChangeDetectorRef);

  readonly windowService = inject(WindowService);

  form: UntypedFormGroup;
  searchTerm = '';
  headers: string[] = this.buildHeaders();
  allRows: any[] = [];
  shownRows: any[] = [];
  loaded = false;
  showAboutThisPage = false;
  overlayTop: string;
  private _noiseLevelsCard: ElementRef;
  private resizeObserver: ResizeObserver;
  @ViewChild('noiseLevelsCard', { read: ElementRef }) set noiseLevelsCard(el: ElementRef) {
    this._noiseLevelsCard = el;
    if (el && this.showAboutThisPage && !this.resizeObserver) {
      this.setupOverlayObserver();
    }
  }
  chartSeries: any;
  chartOptions: any;
  chartXaxis: any;
  chartYaxis: any;
  chartStroke: any;
  chartGrid: any;
  chartTooltip: any;
  @ViewChild('tidyTable') tidyTable: any;
  private panelClosedSubscription: Subscription;

  private mockProperties = [
    { id: 1, name: '123 Main St', color: '#00aaba', status: 'normal' },
    { id: 2, name: '456 Oak Ave', color: '#F9A825', status: 'warning' },
    { id: 3, name: '789 Pine Rd', color: '#C62828', status: 'alert' },
  ];

  constructor() {
    this.form = this.fb.group({
      search: [''],
    });
    this.form.get('search')?.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => this.updateSearch(value));
  }

  async ngOnInit() {
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('noise-monitoring');
    // Demo account renders the same sample data without the about-this-page overlay.
    const showMock = this.showAboutThisPage || this.demoDataService.isDemoAccount();
    this.generateNoiseData();
    if (!showMock) {
      this.chartSeries = [];
    }
    this.headers = this.buildHeaders();
    const rows = showMock ? this.buildRows() : [];
    this.allRows = rows;
    this.shownRows = rows;
    this.loaded = true;
  }

  private setupOverlayObserver() {
    const updateTop = () => {
      if (!this._noiseLevelsCard) {return;}
      const top = this._noiseLevelsCard.nativeElement.getBoundingClientRect().top - 11;
      this.overlayTop = `${top  }px`;
      this.cdr.markForCheck();
    };
    this.resizeObserver = new ResizeObserver(() => updateTop());
    this.resizeObserver.observe(document.body);
    setTimeout(() => updateTop());
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.resizeObserver?.disconnect();
    this.aboutThisPageService.dismissAboutThisPage('noise-monitoring');
    this.chartSeries = [];
    this.allRows = [];
    this.shownRows = [];
    this.cdr.markForCheck();
  }

  private generateNoiseData(): void {
    const dates = [];
    const now = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      dates.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    }

    this.chartSeries = this.mockProperties.map(prop => ({
      name: prop.name,
      data: Array.from({ length: 14 }, () => {
        const base = prop.status === 'alert' ? 75 : prop.status === 'warning' ? 55 : 40;
        return Math.round(base + (Math.random() * 20 - 10));
      })
    }));

    this.chartOptions = {
      type: 'line',
      height: 250,
      toolbar: { show: false },
    };
    this.chartXaxis = {
      categories: dates,
      labels: { style: { colors: '#999', fontSize: '12px' } }
    };
    this.chartYaxis = {
      title: { text: 'Noise Level (dB)' },
      labels: { style: { colors: '#999', fontSize: '12px' }, formatter: (val) => Math.round(val).toString() }
    };
    this.chartStroke = {
      curve: 'smooth',
      width: 2,
      colors: this.mockProperties.map(p => p.color)
    };
    this.chartGrid = { borderColor: '#333', strokeDashArray: 3 };
    this.chartTooltip = {
      enabled: true,
      theme: 'dark',
      y: { formatter: (val) => `${val} dB` }
    };
  }

  buildHeaders(): string[] {
    return ['Property', 'Noise Alert Status', 'last-column'];
  }

  buildRows(): any[] {
    return this.mockProperties.map(prop => ({
      'Property': { value: prop.name },
      'Noise Alert Status': { value: this.getStatusLabel(prop.status), color: this.getStatusColor(prop.status) },
      'last-column': { value: '', hasChevron: true },
      id: prop.id,
    }));
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'normal': return 'Normal';
      case 'warning': return 'Warning';
      case 'alert': return 'Alert';
      default: return status;
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'normal': return '#2E7D32';
      case 'warning': return '#F9A825';
      case 'alert': return '#C62828';
      default: return '#1E2125';
    }
  }

  updateSearch(search: string) {
    this.searchTerm = search || '';
    if (!search || search.length < 2) {
      this.shownRows = [...this.allRows];
      return;
    }
    const term = search.toLowerCase();
    this.shownRows = this.allRows.filter(row =>
      row['Property'].value.toLowerCase().includes(term)
    );
  }

  openHardwareInfo() {
    this.goToPage('hardware-info', { hardwareType: 'noise' }, HardwareInfoPage);
  }

  openOrderSensor() {
    if (this.demoBlock.block()) { return; }
    this.goToPage('order-sensor', { sourceContext: 'noise' }, OrderSensorComponent);
  }

  onTableColumnClick(event: { column: string; element: any }) {
    const property = this.mockProperties.find(p => p.id === event.element.id);
    if (!property) {
      return;
    }
    const params = { propertyName: property.name, propertyId: property.id };
    this.goToPage(`noise-detail/${property.id}`, params, NoiseDetailPage);
  }

  async goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        // Reload data when panel closes if needed
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
